// The stage: images and texts with a Transformer, mask layer, ruler. State is controlled by
// the parent and lives in output pixel space. Every image is alike: tap to select, drag to
// move, handles to scale and rotate; wheel, pinch and the zoom slider act on the selected one.

import Konva from 'konva';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Circle, Image as KImage, Layer, Rect, Stage, Text, Transformer } from 'react-konva';
import type { ImageObject, TextObject } from './design';
import { fitStage, zoomAt, type Geometry } from './geometry';
import type { LoadedImage } from './image';
import { RULER_PX, Ruler } from './Ruler';
import { centreNode, textConfig } from './text';

Konva.hitOnDragEnabled = true; // needed for pinch while a drag is in progress
// A shaky tap must not count as a drag: a portrait photo is cover-fitted with zero sideways slack,
// so Konva's default 3 px nudged it ~30 output px and left a white sliver hidden against the white
// die line. 6 px filters the shake without making a real drag feel sticky (10 did).
Konva.dragDistance = 6;

const PAD = 20;

interface Props {
  g: Geometry;
  images: ImageObject[];
  loaded: Record<string, LoadedImage>;
  onImageChange(im: ImageObject): void;
  texts: TextObject[];
  onTextChange(t: TextObject): void;
  selected: string | null;
  onSelect(id: string | null): void;
  onRemove(): void;
  ruler: boolean;
  fontsVersion: number;
}

export function Editor({ g, images, loaded, onImageChange, texts, onTextChange, selected, onSelect, onRemove, ruler, fontsVersion }: Props) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 300, h: 300 });
  useEffect(() => {
    const el = boxRef.current!;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth - 16, h: el.clientHeight - 16 }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // With the ruler on, the canvas gives up a margin above and to the left; all layers shift by R.
  const R = ruler ? RULER_PX : 0;
  // A band of PAD stage px around the canvas where the photo still shows, darkened like the rest
  // of the cut-off area. A round die line runs 1 px inside the canvas edge, and without the band
  // it is impossible to tell "just inside the circle" from "hanging over the edge".
  const O = R + PAD;
  const { width, height, s } = fitStage(g, box.w - R - 2 * PAD, box.h - R - 2 * PAD);

  // Wheel and pinch zoom the selected image, or the bottom one when nothing is selected.
  const zoomTarget = images.find((im) => im.id === selected) ?? images[0];
  const pointerToOutput = (stage: Konva.Stage) => {
    const p = stage.getPointerPosition() ?? { x: O + width / 2, y: O + height / 2 };
    return { x: (p.x - O) / s, y: (p.y - O) / s };
  };
  const onWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    if (zoomTarget) onImageChange(zoomAt(zoomTarget, e.evt.deltaY < 0 ? 1.08 : 1 / 1.08, pointerToOutput(e.target.getStage()!)));
  };

  // Pinch state lives in a ref: it changes on every touchmove and never needs a render.
  // Scale is computed from the pinch's start, so touchmoves between renders cannot compound.
  const pinch = useRef<{ dist: number; mid: { x: number; y: number }; image: ImageObject } | null>(null);
  const onTouchMove = (e: Konva.KonvaEventObject<TouchEvent>) => {
    const [a, b] = [e.evt.touches[0], e.evt.touches[1]];
    if (!a || !b || !zoomTarget) return;
    e.evt.preventDefault();
    const stage = e.target.getStage()!;
    const rect = stage.container().getBoundingClientRect();
    const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    const mid = { x: ((a.clientX + b.clientX) / 2 - rect.left - O) / s, y: ((a.clientY + b.clientY) / 2 - rect.top - O) / s };
    if (!pinch.current) {
      // Second finger landed: whatever the first one was dragging stops, the pinch takes over.
      stage.find((n: Konva.Node) => n.isDragging()).forEach((n) => n.stopDrag());
      pinch.current = { dist, mid, image: zoomTarget };
      return;
    }
    onImageChange(zoomAt(pinch.current.image, dist / pinch.current.dist, pinch.current.mid));
  };
  const onTouchEnd = (e: Konva.KonvaEventObject<TouchEvent>) => {
    if (e.evt.touches.length < 2) pinch.current = null;
  };

  const trimW = g.trimWidthMm * g.pxPerMm * s;
  const trimH = g.trimHeightMm * g.pxPerMm * s;
  const cx = (g.outW / 2) * s, cy = (g.outH / 2) * s;

  // Transformer follows the selected node; a trash button floats at its top-right corner
  // (inside the canvas corner when the node extends past the canvas, as a cover photo does).
  // The button is HTML, not Konva: real button semantics and a proper tap target for free.
  const trRef = useRef<Konva.Transformer>(null);
  const nodeRefs = useRef(new Map<string, Konva.Node>());
  const [trash, setTrash] = useState<{ x: number; y: number } | null>(null);
  useLayoutEffect(() => {
    const tr = trRef.current;
    if (!tr) return;
    const node = selected ? nodeRefs.current.get(selected) : undefined;
    tr.nodes(node ? [node] : []);
    tr.getLayer()?.batchDraw();
    if (!node || !boxRef.current) { setTrash(null); return; }
    const place = () => {
      const c = node.getStage()!.container().getBoundingClientRect();
      const b = boxRef.current!.getBoundingClientRect();
      const r = node.getClientRect();
      const x = Math.min(r.x + r.width + 4, O + width + PAD - 32), y = Math.max(r.y - 30, R + 8);
      const next = { x: Math.round(c.left - b.left + x), y: Math.round(c.top - b.top + y) };
      setTrash((prev) => (prev && prev.x === next.x && prev.y === next.y ? prev : next));
    };
    place();
    // Konva moves the node without React knowing until the drag ends; follow it live.
    node.on('dragmove.trash transform.trash', place);
    return () => { node.off('dragmove.trash transform.trash'); };
  });

  const band = { x: -PAD, y: -PAD, width: width + 2 * PAD, height: height + 2 * PAD };
  const clip = { clipX: band.x, clipY: band.y, clipWidth: band.width, clipHeight: band.height };

  return (
    <div className="stagebox" ref={boxRef}>
      {trash && (
        <button type="button" className="trash" style={{ left: trash.x, top: trash.y }} aria-label="Ta bort" title="Ta bort" onClick={onRemove}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 10v7M14 10v7" />
          </svg>
        </button>
      )}
      <Stage width={width + R + 2 * PAD} height={height + R + 2 * PAD} onWheel={onWheel} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        onClick={(e) => e.target === e.target.getStage() && onSelect(null)} onTap={(e) => e.target === e.target.getStage() && onSelect(null)}>
        <Layer x={O} y={O} {...clip}>
          {/* What the customer sees as empty prints white: the frosting sheet is white. */}
          <Rect x={0} y={0} width={width} height={height} fill="#fff" onClick={() => onSelect(null)} onTap={() => onSelect(null)} />
          {images.map((im) => loaded[im.id] && (
            <ImageNode key={im.id} im={im} src={loaded[im.id]} s={s}
              register={(n) => (n ? nodeRefs.current.set(im.id, n) : nodeRefs.current.delete(im.id))}
              onSelect={() => onSelect(im.id)} onChange={onImageChange} />
          ))}
          {texts.map((t) => (
            <TextNode key={t.id} t={t} s={s} fontsVersion={fontsVersion} selected={t.id === selected}
              register={(n) => (n ? nodeRefs.current.set(t.id, n) : nodeRefs.current.delete(t.id))}
              onSelect={() => onSelect(t.id)} onChange={onTextChange} />
          ))}
        </Layer>
        <Layer x={O} y={O} listening={false}>
          {/* Everything outside the die line is what gets cut off: dark it, then punch the trim hole. */}
          <Rect {...band} fill="rgba(0,0,0,0.55)" />
          {g.shape === 'circle'
            ? <Circle x={cx} y={cy} radius={trimW / 2} fill="#000" globalCompositeOperation="destination-out" />
            : <Rect x={cx - trimW / 2} y={cy - trimH / 2} width={trimW} height={trimH} fill="#000" globalCompositeOperation="destination-out" />}
          {g.shape === 'circle'
            ? <Circle x={cx} y={cy} radius={trimW / 2} stroke="#fff" strokeWidth={1} />
            : <Rect x={cx - trimW / 2} y={cy - trimH / 2} width={trimW} height={trimH} stroke="#fff" strokeWidth={1} />}
        </Layer>
        {/* Handles live in their own unclipped layer: a cover photo's corners and rotate knob sit
            outside the canvas, and a clipped layer would hide them. */}
        <Layer x={O} y={O}>
          <Transformer ref={trRef} enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
            anchorSize={12} borderStroke="#d6336c" anchorStroke="#d6336c" rotateAnchorOffset={28}
            boundBoxFunc={(oldBox, newBox) => (newBox.width < 12 ? oldBox : newBox)} />
        </Layer>
        {ruler && <Layer listening={false}><Ruler g={g} s={s} o={O} /></Layer>}
      </Stage>
    </div>
  );
}

function ImageNode({ im, src, s, register, onSelect, onChange }: {
  im: ImageObject; src: LoadedImage; s: number;
  register(n: Konva.Image | null): void; onSelect(): void; onChange(im: ImageObject): void;
}) {
  const k = (im.scale / src.workingScale) * s;
  // Scale from the Transformer becomes im.scale; the node itself goes back to the stage scale.
  // node.x() is relative to the layer, so no ruler offset here.
  const commit = (n: Konva.Image) => {
    const f = n.scaleX() / k;
    n.scaleX(k); n.scaleY(k);
    onChange({ ...im, scale: im.scale * f, rotation: n.rotation(), centerX: n.x() / s, centerY: n.y() / s });
  };
  return (
    <KImage ref={register} image={src.working}
      x={im.centerX * s} y={im.centerY * s} offsetX={src.working.width / 2} offsetY={src.working.height / 2}
      scaleX={k} scaleY={k} rotation={im.rotation}
      draggable onDragStart={onSelect} onClick={onSelect} onTap={onSelect}
      onDragEnd={(e) => commit(e.target as Konva.Image)} onTransformEnd={(e) => commit(e.target as Konva.Image)} />
  );
}

function TextNode({ t, s, fontsVersion, selected, register, onSelect, onChange }: {
  t: TextObject; s: number; fontsVersion: number; selected: boolean;
  register(n: Konva.Text | null): void; onSelect(): void; onChange(t: TextObject): void;
}) {
  const ref = useRef<Konva.Text>(null);
  // Re-centre whenever anything that changes the measured size changes (text, font, size, fonts loaded).
  useLayoutEffect(() => {
    const n = ref.current;
    if (!n) return;
    centreNode(n);
    n.getLayer()?.batchDraw();
  }, [t.text, t.font, t.fontSizePx, s, fontsVersion]);

  const commit = (n: Konva.Text) => {
    // Scale from the Transformer becomes font size; the node itself stays at scale 1.
    const k = n.scaleX();
    n.scaleX(1); n.scaleY(1);
    onChange({ ...t, fontSizePx: Math.max(8, t.fontSizePx * k), rotation: n.rotation(), centerX: n.x() / s, centerY: n.y() / s });
  };

  return (
    <Text ref={(n) => { (ref as React.MutableRefObject<Konva.Text | null>).current = n; register(n); }}
      {...textConfig(t, s)}
      // A tap selects; only the selected text can be dragged, so a swipe over an unselected
      // text moves the image under it instead of the text.
      draggable={selected}
      onClick={onSelect} onTap={onSelect}
      onDragEnd={(e) => commit(e.target as Konva.Text)}
      onTransformEnd={(e) => commit(e.target as Konva.Text)} />
  );
}
